/**
 * integrationDocs.js — o conteúdo da documentação de integração
 * CI Sistemas de Informática Ltda. — FastALPR Parking (Onda 64)
 *
 * A documentação vivia fora da plataforma, publicada como página avulsa. Isso
 * exigia autenticação de terceiros para abrir — barreira sem sentido para quem
 * já tem login aqui, e pior ainda para o time que precisa dela aberta ao lado
 * do editor enquanto escreve o integrador.
 *
 * Agora ela mora dentro do sistema, protegida pelo mesmo login, e o conteúdo é
 * DADO, não marcação: as duas verticais saem da mesma fonte, com as diferenças
 * concentradas no objeto `VERTICAIS`. Duas páginas escritas à mão divergiriam
 * na primeira correção feita só de um lado.
 *
 * Formato dos blocos, renderizados por `pages/IntegrationDocs.js`:
 *   { t: 'h2'|'h3', txt, id? }        títulos (h2 entra no sumário)
 *   { t: 'p', txt }                    parágrafo (aceita <strong>, <code>, <em>)
 *   { t: 'lista', itens: [] }          lista simples
 *   { t: 'passos', itens: [] }         lista numerada
 *   { t: 'tabela', cab: [], linhas: [] }
 *   { t: 'codigo', txt, rotulo? }      bloco monoespaçado
 *   { t: 'endpoint', metodo, caminho, escopo }
 *   { t: 'nota', variante: 'info'|'aviso'|'perigo', txt }
 *   { t: 'parte', tag, titulo, dir }   divisor de parte
 */

// Exportado desde a Onda 107: a tela de Chaves de API monta os escopos de cada
// vertical a partir deste mesmo mapa, para a tela e a documentação não poderem
// dizer coisas diferentes sobre qual escopo vale onde.
export const VERTICAIS = {
  clube_social: {
    chave: 'clube_social',
    nome: 'Clube Social',
    titulo: 'Integração Clube Social',
    evento: 'club.plate.recognized.v1',
    baseCadastro: '/api/integration/club',
    titular: 'associado',
    titulares: 'associados',
    Titular: 'Associado',
    codigo: 'member_code',
    recurso: 'members',
    campoLista: 'members',
    exemploCodigo: 'A-10482',
    escritaCadastro: 'club_members:write',
    leituraCadastro: 'club_members:read',
    leituraEventos: 'club_events:read',
    clAtivo: 'member',
    clInativo: 'inactive_member',
    descAtivo: 'Veículo de associado ativo',
    descInativo: 'Veículo de associado inativo',
    campoIdentidade: 'member_code',
    hostPrincipal: 'member',
    hostDesc: 'um associado autorizou o prestador',
    hostCodigo: 'member_code',
    gestao: 'a gestão do clube',
    temUnidade: false,
    tamNome: 180,
    tamMarca: 100,
  },
  condominio: {
    chave: 'condominio',
    nome: 'Condomínio Residencial',
    titulo: 'Integração Condomínio Residencial',
    evento: 'condo.plate.recognized.v1',
    baseCadastro: '/api/integration',
    titular: 'unidade',
    titulares: 'unidades',
    Titular: 'Unidade',
    codigo: 'unit_code',
    recurso: 'units',
    campoLista: 'units',
    exemploCodigo: 'AP-0501-B',
    escritaCadastro: 'residents:write',
    leituraCadastro: 'residents:read',
    leituraEventos: 'access:read',
    clAtivo: 'resident',
    clInativo: 'inactive_resident',
    descAtivo: 'Veículo de morador ativo',
    descInativo: 'Veículo de morador inativo',
    campoIdentidade: 'unit_code',
    hostPrincipal: 'resident',
    hostDesc: 'o morador da unidade autorizou a visita',
    hostCodigo: 'unit_code',
    gestao: 'a administração do condomínio',
    temUnidade: true,
    tamNome: 150,
    tamMarca: 80,
  },
};

export const LISTA_VERTICAIS = Object.values(VERTICAIS).map(v => ({ chave: v.chave, nome: v.nome, titulo: v.titulo }));

function escopos(V) {
  return [
    [V.escritaCadastro, `Criar e atualizar ${V.titulares}, veículos e sincronizações`],
    [V.leituraCadastro, 'Consultar o cadastro já carregado'],
    [V.leituraEventos, 'Consultar o histórico de eventos de reconhecimento'],
    ['visitors:write', 'Cadastrar visitantes e autorizar visitas'],
    ['visitors:read', 'Consultar visitantes e autorizações'],
    ['exits:write', 'Registrar o pagamento de um ticket (liberação da saída) e cancelá-lo'],
    ['exits:read', 'Consultar as autorizações de saída'],
  ];
}

/** Monta a documentação inteira de uma vertical. */
export function documentacao(chave) {
  const V = VERTICAIS[chave] || VERTICAIS.clube_social;
  const irma = chave === 'condominio' ? VERTICAIS.clube_social : VERTICAIS.condominio;
  const B = V.baseCadastro;
  const b = []; // blocos

  // ── Abertura ─────────────────────────────────────────────────────────────
  b.push({ t: 'p', txt: `Documentação técnica para quem escreve o integrador do sistema de gestão. Cobre os <strong>três caminhos</strong> da integração: a carga de ${V.titulares} e veículos que vocês enviam, o cadastro de visitantes com a autorização de cada visita, e os eventos de placa reconhecida que nós entregamos.` });
  b.push({ t: 'nota', variante: 'info', txt: `O mecanismo é o <strong>mesmo</strong> nas duas verticais — muda o vocabulário, não o comportamento. Quem já integrou com a <em>${irma.nome}</em> reaproveita praticamente tudo: só troca ${irma.codigo} por <code>${V.codigo}</code> e o endereço do cadastro.` });

  b.push({ t: 'h2', id: 'responsabilidades', txt: 'Quem faz o quê' });
  b.push({ t: 'p', txt: 'Nesta vertical o FastALPR <strong>não cria ticket, não calcula tarifa, não controla tolerância e não comanda cancela</strong>. Ele reconhece a placa, classifica o veículo contra o cadastro que vocês enviam e entrega o evento. O sistema de gestão continua sendo a fonte de verdade de permanência, cobrança e liberação.' });
  b.push({ t: 'tabela', cab: ['Responsabilidade', 'FastALPR', 'Sistema de gestão'], linhas: [
    ['Reconhecer placa e confiança', 'sim', '—'],
    [`Classificar ${V.titular}, marca e modelo`, 'sim', 'fornece o cadastro'],
    ['Reconhecer visita autorizada', 'sim', 'envia a autorização'],
    ['Determinar entrada ou saída', 'sim, pela câmera', 'consome o dado'],
    ['Criar ticket e cobrar', '—', 'sim'],
    ['Abrir cancela', '—', 'sim'],
    ['Entregar o evento assinado', 'sim', 'recebe e confirma'],
  ]});

  // ── PARTE A ──────────────────────────────────────────────────────────────
  b.push({ t: 'parte', tag: 'Parte A', titulo: `Carga de ${V.titulares} e veículos`, dir: `Vocês chamam · nós recebemos · base <code>${B}</code>` });

  b.push({ t: 'h2', id: 'autenticacao', txt: 'Autenticação' });
  b.push({ t: 'p', txt: 'Toda chamada exige uma API Key, gerada na tela <em>API de Integração</em> e entregue uma única vez. Envie em um destes cabeçalhos:' });
  b.push({ t: 'codigo', txt: 'X-API-Key: <sua-chave>\n\n— ou —\n\nAuthorization: Bearer <sua-chave>' });
  b.push({ t: 'p', txt: `A chave carrega escopos. Para a carga do cadastro, o necessário é <code>${V.escritaCadastro}</code>.` });
  b.push({ t: 'tabela', cab: ['Escopo', 'Permite'], linhas: escopos(V).map(([e, d]) => [`<code>${e}</code>`, d]) });
  b.push({ t: 'p', txt: 'Sem chave: <code>401</code>. Chave válida sem o escopo exigido: <code>403</code>, com a lista de escopos requeridos e os da chave no corpo da resposta.' });

  b.push({ t: 'h2', id: 'idempotencia', txt: 'Idempotência' });
  b.push({ t: 'p', txt: 'Todas as rotas de escrita <strong>exigem</strong> o cabeçalho <code>Idempotency-Key</code>, de até 128 caracteres. Ele torna seguro repetir uma chamada após um tempo limite ou uma queda de conexão, sem risco de aplicar a carga duas vezes.' });
  b.push({ t: 'codigo', txt: 'Idempotency-Key: carga-2026-09-09-lote-001' });
  b.push({ t: 'lista', itens: [
    'Chave repetida com o <strong>mesmo</strong> corpo — devolvemos a resposta original guardada, sem reprocessar.',
    'Chave repetida com corpo <strong>diferente</strong> — <code>409 Conflict</code>. Isso protege contra reuso acidental.',
    'As respostas ficam guardadas por <strong>24 horas</strong>.',
  ]});

  b.push({ t: 'h2', id: 'cadastro', txt: `${V.Titular} e seus veículos` });
  b.push({ t: 'p', txt: `${V.temUnidade ? 'A unidade é identificada' : 'O associado é identificado'} pelo <code>${V.codigo}</code> — o identificador de vocês, estável entre as cargas. Cada veículo é identificado pelo <code>external_vehicle_id</code>, também de vocês. Nós nunca inventamos identificadores: é por eles que o cadastro se mantém coerente entre uma carga e outra.` });

  const camposTitular = [
    [`<code>${V.codigo}</code>`, 'obrigatório', 'string', 'Até 64 caracteres. Vai na URL, não no corpo.'],
    ['<code>name</code>', 'obrigatório', 'string', `Até ${V.tamNome} caracteres.${V.temUnidade ? ' Nome do titular da unidade.' : ''}`],
  ];
  if (V.temUnidade) {
    camposTitular.push(
      ['<code>unit</code>', 'obrigatório', 'string', 'Até 30 caracteres. O número do apartamento, como a portaria o lê.'],
      ['<code>block</code>', 'condicional', 'string', 'Até 30 caracteres. Ver o aviso abaixo.'],
      ['<code>document</code>', 'opcional', 'string', 'Até 20 caracteres.'],
      ['<code>phone</code>', 'opcional', 'string', 'Até 20 caracteres.'],
      ['<code>email</code>', 'opcional', 'string', 'Até 150 caracteres.'],
    );
  }
  camposTitular.push(
    ['<code>status</code>', 'opcional', 'enum', '<code>active</code> ou <code>inactive</code>. Padrão <code>active</code>.'],
    ['<code>external_updated_at</code>', 'opcional', 'ISO 8601', 'Quando o registro mudou no sistema de vocês.'],
    ['<code>vehicles</code>', 'opcional', 'lista', 'Os veículos. Lista vazia é aceita.'],
  );
  b.push({ t: 'h3', txt: `Campos ${V.temUnidade ? 'da unidade' : 'do associado'}` });
  b.push({ t: 'tabela', cab: ['Campo', '', 'Tipo', 'Regras'], linhas: camposTitular });

  if (V.temUnidade) {
    b.push({ t: 'nota', variante: 'aviso', txt: 'O <strong>bloco deixa de ser opcional assim que a numeração se repete</strong>. A unidade 101 do bloco A e a 101 do bloco B são unidades diferentes, e sem o bloco nem a portaria nem o relatório conseguem separá-las. Se vocês enviarem uma unidade sem <code>block</code> cujo número já exista em outro bloco, a chamada é recusada com <code>409</code> e uma mensagem dizendo exatamente isso. Em condomínio de torre única, o campo pode ser omitido.' });
  }

  b.push({ t: 'h3', txt: 'Campos de cada veículo' });
  const camposVeic = [
    ['<code>external_vehicle_id</code>', 'obrigatório', 'string', 'Até 100 caracteres. É a chave do veículo.'],
    ['<code>plate</code>', 'obrigatório', 'string', '7 a 8 caracteres. Normalizamos removendo hífen e espaços.'],
    ['<code>brand</code>', 'opcional', 'string', `Até ${V.tamMarca} caracteres.`],
    ['<code>model</code>', 'opcional', 'string', `Até ${V.tamMarca} caracteres.`],
  ];
  if (V.temUnidade) camposVeic.push(['<code>color</code>', 'opcional', 'string', 'Até 50 caracteres.']);
  camposVeic.push(
    ['<code>status</code>', 'opcional', 'enum', '<code>active</code> ou <code>inactive</code>. Padrão <code>active</code>.'],
    ['<code>valid_from</code>', 'opcional', 'ISO 8601', 'Início da validade do vínculo.'],
    ['<code>valid_until</code>', 'opcional', 'ISO 8601', 'Fim da validade do vínculo.'],
    ['<code>external_updated_at</code>', 'opcional', 'ISO 8601', 'Quando o veículo mudou no sistema de vocês.'],
  );
  b.push({ t: 'tabela', cab: ['Campo', '', 'Tipo', 'Regras'], linhas: camposVeic });

  if (V.temUnidade) {
    b.push({ t: 'nota', variante: 'info', txt: 'Sobre <code>status</code>: o contrato fala <code>active</code>/<code>inactive</code> nas duas verticais, para que vocês escrevam um integrador só. Internamente o banco do condomínio grava <code>ativo</code>/<code>bloqueado</code> — a tradução é nossa, e vocês não precisam saber dela.' });
  }
  b.push({ t: 'nota', variante: 'perigo', txt: 'Uma <strong>placa ativa pertence a um único veículo</strong>. Se vocês enviarem uma placa <code>active</code> que já está ativa sob outro <code>external_vehicle_id</code>, a operação falha com <code>409</code>. Para transferir uma placa entre veículos, marque o antigo como <code>inactive</code> na mesma carga ou antes dela.' });

  b.push({ t: 'h2', id: 'individual', txt: 'Carga individual' });
  b.push({ t: 'endpoint', metodo: 'PUT', caminho: `${B}/${V.recurso}/:${V.codigo}`, escopo: V.escritaCadastro });
  b.push({ t: 'codigo', rotulo: 'exemplo', txt:
`curl -X PUT "${B}/${V.recurso}/${V.exemploCodigo}" \\
  -H "X-API-Key: <sua-chave>" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: ${V.recurso}-${V.exemploCodigo}-v3" \\
  -d '{
    "name": "Maria Helena Duarte",${V.temUnidade ? '\n    "unit": "501",\n    "block": "B",' : ''}
    "status": "active",
    "external_updated_at": "2026-09-09T11:20:00Z",
    "vehicles": [
      {
        "external_vehicle_id": "VEIC-77120",
        "plate": "BRQ0F75",
        "brand": "Toyota",
        "model": "Corolla",
        "status": "active",
        "valid_until": "2027-01-31"
      }
    ]
  }'` });
  b.push({ t: 'p', txt: 'Resposta <code>201</code> quando o registro é criado, <code>200</code> quando é atualizado.' });
  b.push({ t: 'codigo', rotulo: 'resposta', txt:
`{
  "success": true,
  "data": {
    "${V.temUnidade ? 'resident_id' : 'member_id'}": 42,
    "${V.temUnidade ? 'unit_created' : 'member_created'}": true,
    "created": 1,      // veículos criados
    "updated": 0       // veículos atualizados
  }
}` });

  b.push({ t: 'h2', id: 'lote', txt: 'Carga em lote' });
  b.push({ t: 'p', txt: `Aplica de <strong>1 a 500 ${V.titulares}</strong> por chamada. Cada item é processado em transação própria: um erro em um não desfaz os demais. O corpo da requisição pode ter até <strong>10 MB</strong> — folga para 500 itens com dezenas de veículos cada; acima disso a resposta é <code>413</code>.` });
  b.push({ t: 'endpoint', metodo: 'POST', caminho: `${B}/${V.recurso}/batch`, escopo: V.escritaCadastro });
  b.push({ t: 'codigo', txt:
`{
  "external_sync_id": "sync-2026-09-09-full",   // opcional; só na carga total
  "${V.campoLista}": [
    {
      "${V.codigo}": "${V.exemploCodigo}",
      "name": "Maria Helena Duarte",${V.temUnidade ? '\n      "unit": "501", "block": "B",' : ''}
      "status": "active",
      "vehicles": [
        { "external_vehicle_id": "VEIC-77120", "plate": "BRQ0F75" }
      ]
    }
  ]
}` });
  b.push({ t: 'p', txt: 'Resposta <code>200</code> quando todos os itens passam, <code>207</code> quando há falhas parciais. O corpo traz o resultado item a item, na ordem enviada.' });

  b.push({ t: 'h2', id: 'completa', txt: 'Sincronização completa' });
  b.push({ t: 'p', txt: 'Quando vocês quiserem enviar o cadastro inteiro e fazer com que <em>o que não veio</em> seja considerado inativo, envolvam os lotes em uma sincronização. São três passos.' });
  b.push({ t: 'passos', itens: [
    `<strong>Abrir</strong> — <code>POST ${B}/sync-runs</code> com <code>external_sync_id</code> (seu identificador, até 100 caracteres), <code>mode</code> (<code>full</code> ou <code>incremental</code>) e, opcionalmente, <code>expected_items</code>.`,
    `<strong>Enviar os lotes</strong> — chame <code>/${V.recurso}/batch</code> quantas vezes forem necessárias, sempre com o mesmo <code>external_sync_id</code> no corpo.`,
    `<strong>Encerrar</strong> — <code>POST ${B}/sync-runs/:external_sync_id/complete</code>.`,
  ]});
  b.push({ t: 'nota', variante: 'info', txt: `<strong>Nada é apagado, nunca.</strong> No encerramento de uma sincronização <code>full</code>, os registros que não apareceram em nenhum lote são marcados como <strong>inativos</strong>, com data e motivo. O histórico é preservado, e é isso que permite a classificação <code>${V.clInativo}</code> quando um veículo desligado aparecer na câmera.${V.temUnidade ? ' <strong>O snapshot não alcança o cadastro feito na portaria:</strong> só entram os registros que têm código externo <em>e</em> vieram de uma chave de API.' : ''}` });

  // ── Onda 111 ──────────────────────────────────────────────────────────────
  // A pergunta que o Iatagã fez depois da primeira carga real do Ipê: "é
  // necessário sempre realizar a carga completa, ou podemos manter só as novas
  // inclusões e exclusões?". A resposta depende de um detalhe que nem sempre
  // salta aos olhos — fora do `full`, NADA é inativado por omissão — e errar
  // nisso deixa sócio desligado com a cancela liberada.
  b.push({ t: 'h2', id: 'periodica', txt: 'Carga periódica: completa ou incremental?' });
  b.push({ t: 'p', txt: `Depois da carga inicial, dá para manter o cadastro só com as mudanças. A condição é uma só, e é a parte que costuma passar despercebida: <strong>fora da sincronização completa, nada é inativado por omissão</strong>. Deixar de enviar ${V.temUnidade ? 'uma unidade' : 'um associado'} não o desliga aqui — ${V.temUnidade ? 'ela continua ativa' : 'ele continua ativo'}, com o veículo liberado na cancela.` });
  b.push({ t: 'tabela', cab: ['Situação', 'O que enviar'], linhas: [
    [`${V.Titular} ${V.temUnidade ? 'nova' : 'novo'}`, `${V.temUnidade ? 'a unidade' : 'o associado'}, com os veículos`],
    ['Veículo novo', `${V.temUnidade ? 'a unidade' : 'o associado'}, com o veículo na lista`],
    [`<strong>${V.Titular} ${V.temUnidade ? 'desativada' : 'desligado'}</strong>`, `${V.temUnidade ? 'a mesma unidade' : 'o mesmo associado'} com <code>status: "inactive"</code>`],
    ['<strong>Veículo removido</strong>', 'o veículo com <code>status: "inactive"</code> na lista'],
    ['Veículo que mudou de dono', `o mesmo <code>external_vehicle_id</code> com o novo <code>${V.codigo}</code>`],
  ]});
  b.push({ t: 'p', txt: 'Omitir um veículo da lista não faz nada: o que não vem no corpo não é tocado. A inativação é sempre explícita.' });
  b.push({ t: 'codigo', rotulo: 'Desligamento no modo incremental', txt: `{
  "${V.codigo}": "${V.exemploCodigo}",
  "name": "${V.temUnidade ? 'Apartamento 501 B' : 'Maria Souza'}",
  "status": "inactive",
  "vehicles": [
    { "external_vehicle_id": "VEIC-77120", "plate": "BRQ0F75", "status": "inactive" }
  ]
}` });
  b.push({ t: 'p', txt: `A <strong>sincronização completa continua tendo função</strong>: ela é a rede de segurança. O incremental perde evento em três situações reais — requisição que falhou e não foi reenviada, janela em que a plataforma esteve fora do ar, e alteração feita direto no banco de vocês sem gerar evento. Recomendamos <strong>incremental no dia a dia e uma <code>full</code> por semana</strong>, fora do horário de movimento.` });
  b.push({ t: 'p', txt: 'O <code>complete</code> de uma sincronização <code>incremental</code> apenas encerra a execução: <strong>não inativa nada</strong>. Só o <code>full</code> inativa por ausência.' });
  b.push({ t: 'nota', variante: 'perigo', txt: `<strong>Só encerre uma <code>full</code> depois de conferir que nenhum lote teve item recusado.</strong> O encerramento inativa tudo o que não apareceu — se um lote falhou no meio, o estrago é proporcional. Numa carga real de 22/09/2026, com 7,5% dos itens recusados, encerrar o snapshot teria desligado 295 ${V.titulares} de uma vez. O gestor vê isso em uma linha na tela <em>Cargas da Integração</em>, e vocês veem no corpo da resposta: <code>207</code> significa que houve item recusado.` });

  b.push({ t: 'h2', id: 'consulta', txt: 'Consultas' });
  b.push({ t: 'endpoint', metodo: 'GET', caminho: `${B}/${V.recurso}`, escopo: V.leituraCadastro });
  b.push({ t: 'p', txt: 'Devolve o cadastro como está no FastALPR — útil para conferir o resultado de uma carga.' });
  b.push({ t: 'endpoint', metodo: 'GET', caminho: `${B}/events`, escopo: V.leituraEventos });
  b.push({ t: 'p', txt: 'Histórico dos eventos de reconhecimento. Serve como conferência ou reconciliação, caso algum webhook tenha se perdido do lado de vocês.' });

  b.push({ t: 'h2', id: 'erros', txt: 'Erros da API' });
  b.push({ t: 'p', txt: 'Todas as respostas de erro seguem o mesmo formato: <code>{ "success": false, "error": "mensagem" }</code>.' });
  b.push({ t: 'tabela', cab: ['Código', 'Quando acontece'], linhas: [
    ['<code>400</code>', 'Campo obrigatório ausente, tipo errado, placa inválida, <code>Idempotency-Key</code> faltando, lote fora do intervalo de 1 a 500.'],
    ['<code>401</code>', 'API Key ausente, inválida ou revogada.'],
    ['<code>403</code>', 'Chave válida, mas sem o escopo exigido pela rota.'],
    ['<code>404</code>', 'Sincronização, visitante ou autorização não encontrados.'],
    ['<code>409</code>', `Placa ativa já pertence a outro veículo; <code>Idempotency-Key</code> reusada com corpo diferente; autorização de visita já em aberto para a placa${V.temUnidade ? '; unidade sem bloco cuja numeração já existe em outro bloco' : ''}.`],
    ['<code>413</code>', 'Corpo da requisição acima de 10 MB. Dividam a carga em lotes menores.'],
    ['<code>207</code>', 'Não é erro: lote aplicado com falhas parciais. Verifiquem item a item.'],
  ]});

  // ── PARTE B — visitantes ─────────────────────────────────────────────────
  b.push({ t: 'parte', tag: 'Parte B', titulo: 'Visitantes e autorização de visita', dir: 'Vocês chamam · nós recebemos · base <code>/api/integration</code>' });

  b.push({ t: 'h2', id: 'visitantes', txt: 'O modelo: cadastro e visita são coisas diferentes' });
  b.push({ t: 'tabela', cab: ['', 'O cadastro do visitante', 'A autorização de visita'], linhas: [
    ['Responde', 'quem é a pessoa', 'quando ela pode entrar'],
    ['Enviado', 'uma vez', 'a cada nova visita'],
    ['Contém', 'nome, documento, telefone, placa', 'placa, janela de validade, quem autorizou'],
    ['Vale', 'enquanto a pessoa existir', '<strong>uma única vez</strong>'],
  ]});
  b.push({ t: 'p', txt: 'Um prestador que vem toda semana tem <strong>um</strong> cadastro e <strong>uma autorização por visita</strong>. Isso é o que preserva o histórico: cada visita fica registrada por si, com a data em que aconteceu e quem a autorizou, em vez de uma linha só que é reescrita e apaga a visita anterior.' });

  b.push({ t: 'h3', txt: 'O ciclo de uma autorização' });
  b.push({ t: 'tabela', cab: ['Situação', 'Significado'], linhas: [
    ['<code>pending</code>', 'Autorizada, a visita ainda não aconteceu.'],
    ['<code>used</code>', 'A câmera de entrada reconheceu a placa. A visita está acontecendo.'],
    ['<code>expired</code>', 'Passou da janela sem que a entrada acontecesse.'],
    ['<code>cancelled</code>', 'Vocês cancelaram antes do uso.'],
  ]});
  b.push({ t: 'nota', variante: 'info', txt: '<strong>A saída não precisa de autorização nova.</strong> A entrada consome a autorização, que passa a <code>used</code>; na saída o sistema reconhece a mesma autorização e o veículo sai normalmente. O que não acontece é uma <em>segunda entrada</em> com a mesma autorização — para isso, envie outra.' });

  b.push({ t: 'h2', id: 'visitante-cadastro', txt: 'Cadastro do visitante' });
  b.push({ t: 'p', txt: 'Endereçado pelo <code>external_visitor_id</code> — o identificador de vocês. Reenviar o mesmo código atualiza o cadastro; não cria um segundo.' });
  b.push({ t: 'endpoint', metodo: 'PUT', caminho: '/api/integration/visitors/external/:external_visitor_id', escopo: 'visitors:write' });
  b.push({ t: 'tabela', cab: ['Campo', '', 'Tipo', 'Regras'], linhas: [
    ['<code>external_visitor_id</code>', 'obrigatório', 'string', 'Até 100 caracteres. Vai na URL.'],
    ['<code>name</code>', 'obrigatório', 'string', 'Até 150 caracteres.'],
    ['<code>plate</code>', 'obrigatório', 'string', '7 a 8 caracteres. A placa habitual da pessoa.'],
    ['<code>document</code>', 'opcional', 'string', 'Até 20 caracteres.'],
    ['<code>phone</code>', 'opcional', 'string', 'Até 20 caracteres.'],
    ['<code>external_updated_at</code>', 'opcional', 'ISO 8601', 'Quando o registro mudou no sistema de vocês.'],
  ]});
  b.push({ t: 'nota', variante: 'aviso', txt: '<strong>O cadastro sozinho não autoriza nada.</strong> Um visitante recém-cadastrado, sem autorização de visita, é recusado na cancela como placa não cadastrada. É intencional: a permissão vem sempre da visita, nunca do cadastro.' });

  b.push({ t: 'h2', id: 'visita', txt: 'Autorização de visita' });
  b.push({ t: 'p', txt: 'Uma chamada por visita. O visitante precisa já estar cadastrado.' });
  b.push({ t: 'endpoint', metodo: 'POST', caminho: '/api/integration/visit-authorizations', escopo: 'visitors:write' });
  b.push({ t: 'tabela', cab: ['Campo', '', 'Tipo', 'Regras'], linhas: [
    ['<code>external_visitor_id</code>', 'obrigatório', 'string', 'O visitante já cadastrado. <code>404</code> se não existir.'],
    ['<code>valid_until</code>', 'obrigatório', 'ISO 8601', 'Fim da janela da visita.'],
    ['<code>host</code>', 'obrigatório', 'objeto', 'Quem autorizou — ver abaixo.'],
    ['<code>valid_from</code>', 'opcional', 'ISO 8601', 'Início da janela. Padrão: agora.'],
    ['<code>plate</code>', 'opcional', 'string', 'A placa <em>desta</em> visita. Padrão: a do cadastro.'],
    ['<code>purpose</code>', 'opcional', 'string', 'Até 255 caracteres. Motivo da visita.'],
    ['<code>external_authorization_id</code>', 'opcional', 'string', 'Seu identificador da visita. Necessário para cancelar depois.'],
  ]});
  b.push({ t: 'h3', txt: 'O objeto <code>host</code>' });
  b.push({ t: 'tabela', cab: ['Forma', 'Quando usar'], linhas: [
    [`<code>{ "type": "${V.hostPrincipal}", "code": "${V.exemploCodigo}" }</code>`, `${V.hostDesc}. O <code>code</code> é o <code>${V.hostCodigo}</code> já carregado — <code>400</code> se não existir.`],
    ['<code>{ "type": "management" }</code>', `${V.gestao} autorizou diretamente, sem vincular a ${V.temUnidade ? 'uma unidade' : 'um associado'}.`],
  ]});
  b.push({ t: 'codigo', rotulo: 'exemplo', txt:
`curl -X POST "/api/integration/visit-authorizations" \\
  -H "X-API-Key: <sua-chave>" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: visita-2026-09-09-00417" \\
  -d '{
    "external_authorization_id": "VISITA-00417",
    "external_visitor_id": "PREST-4471",
    "host": { "type": "${V.hostPrincipal}", "code": "${V.exemploCodigo}" },
    "purpose": "Manutenção hidráulica",
    "valid_from": "2026-09-10T08:00:00Z",
    "valid_until": "2026-09-10T18:00:00Z"
  }'` });
  b.push({ t: 'nota', variante: 'perigo', txt: '<strong>Uma autorização em aberto por placa.</strong> Se já houver uma <code>pending</code> válida para aquela placa, a chamada é recusada com <code>409</code> e o identificador da existente. Duas autorizações sobrepostas tornariam indeterminado qual delas a entrada consome.' });

  b.push({ t: 'h2', id: 'visita-cancelar', txt: 'Cancelar e consultar' });
  b.push({ t: 'endpoint', metodo: 'POST', caminho: '/api/integration/visit-authorizations/:external_authorization_id/cancel', escopo: 'visitors:write' });
  b.push({ t: 'p', txt: 'Cancelar não apaga: o registro permanece, com situação <code>cancelled</code>. Uma autorização já <code>used</code> não pode ser cancelada — devolve <code>409</code>, porque a visita já aconteceu e o histórico não se desfaz.' });
  b.push({ t: 'endpoint', metodo: 'GET', caminho: '/api/integration/visit-authorizations?status=pending&limit=100', escopo: 'visitors:read' });
  b.push({ t: 'p', txt: 'Lista as autorizações com o visitante e o anfitrião resolvidos. O filtro <code>status</code> aceita <code>pending</code>, <code>used</code>, <code>expired</code> e <code>cancelled</code>.' });

  // ── PARTE C — liberação da saída pelo pagamento (Onda 166) ────────────────
  b.push({ t: 'parte', tag: 'Parte C', titulo: 'Liberação da saída pelo pagamento', dir: 'Vocês chamam · nós recebemos · base <code>/api/integration</code>' });

  b.push({ t: 'h2', id: 'saida', txt: 'Autorização de saída (pagamento do ticket)' });
  b.push({ t: 'p', txt: 'Para a instalação cujo sistema de gestão cobra o estacionamento. No ato do pagamento, vocês registram que aquela placa pode sair; quando a câmera de saída lê a placa, a plataforma decide se a cancela ou o portão abre. <strong>O pagamento só autoriza: quem abre é a leitura da placa na saída.</strong> A liberação é ligada em Configurações › Integração; desligada, esta chamada devolve <code>403</code>.' });
  b.push({ t: 'tabela', cab: ['Ordem', 'Abre quando', 'Precisa de pagamento?'], linhas: [
    ['1', 'a saída acontece dentro da tolerância depois da entrada lida (a mesma régua do bloco <code>stay</code>)', 'não'],
    ['2', 'a placa é de visitante com autorização de visita válida', 'não'],
    ['3', 'há autorização de saída pendente da placa, dentro do prazo', 'sim — esta chamada'],
  ]});
  b.push({ t: 'endpoint', metodo: 'POST', caminho: '/api/integration/exit-authorizations', escopo: 'exits:write' });
  b.push({ t: 'tabela', cab: ['Campo', '', 'Tipo', 'Regras'], linhas: [
    ['<code>external_payment_id</code>', 'obrigatório', 'string', 'Até 100 caracteres. O identificador do pagamento no sistema de vocês: reenviar o mesmo devolve o registro existente (<code>200</code>), com outra placa <code>409</code>.'],
    ['<code>plate</code>', 'obrigatório', 'string', 'A placa do veículo, com ou sem hífen.'],
    ['<code>paid_at</code>', 'obrigatório', 'ISO 8601', 'Quando o pagamento foi confirmado, com fuso.'],
    ['<code>entry_event_id</code>', 'recomendado', 'string', 'O <code>event_id</code> do evento de ENTRADA que gerou o ticket. Liga o pagamento à entrada; se a leitura da entrada foi corrigida depois, a placa corrigida também libera.'],
    ['<code>external_ticket_id</code>', 'opcional', 'string', 'Até 100 caracteres, só para rastreio.'],
    ['<code>kind</code>', 'opcional', 'string', '<code>paid</code> (padrão) ou <code>exempt</code> — isenção ou cortesia que vocês queiram liberar sem cobrar.'],
    ['<code>amount_cents</code>', 'opcional', 'inteiro', 'Valor pago em centavos, só para registro.'],
    ['<code>valid_until</code>', 'opcional', 'ISO 8601', 'Até quando a saída vale. Padrão: <code>paid_at</code> + o prazo configurado na plataforma (30 min).'],
  ]});
  b.push({ t: 'codigo', rotulo: 'exemplo', txt:
`curl -X POST "/api/integration/exit-authorizations" \\
  -H "X-API-Key: <sua-chave>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "external_payment_id": "PAG-2026-000123",
    "plate": "EMV7G15",
    "entry_event_id": "evt_d366010384674b0a9c4aeeea51fadea3",
    "external_ticket_id": "TK-88812",
    "kind": "paid",
    "paid_at": "2026-10-15T17:40:00-03:00",
    "amount_cents": 1500
  }'` });
  b.push({ t: 'p', txt: 'Respostas: <code>201</code> com <code>authorization_id</code>, <code>status: "pending"</code> e <code>expires_at</code> (e <code>entry_event_found</code> quando o <code>entry_event_id</code> veio); <code>200</code> no reenvio; <code>409</code> se o pagamento já existe com outra placa; <code>422</code> com <code>field</code> no campo inválido; <code>403</code> com a liberação desligada nesta instalação.' });
  b.push({ t: 'nota', variante: 'aviso', txt: '<strong>Uma saída por pagamento.</strong> A autorização é consumida na primeira saída lida, pela cancela ou pelo portão. Se a câmera ler a placa com um caractere trocado por confusão (O/0, I/1…) e houver UMA só autorização pendente compatível, ela vale.' });

  b.push({ t: 'h2', id: 'saida-cancelar', txt: 'Cancelar e consultar' });
  b.push({ t: 'endpoint', metodo: 'POST', caminho: '/api/integration/exit-authorizations/:external_payment_id/cancel', escopo: 'exits:write' });
  b.push({ t: 'p', txt: 'Estorno ou pagamento desfeito antes da saída. Cancelar não apaga: o registro fica, com situação <code>cancelled</code>. Uma autorização já <code>used</code> não pode ser cancelada — <code>409</code>, com <code>used_at</code>.' });
  b.push({ t: 'endpoint', metodo: 'GET', caminho: '/api/integration/exit-authorizations?status=pending&plate=EMV7G15&limit=100', escopo: 'exits:read' });
  b.push({ t: 'p', txt: 'Filtros <code>status</code> (<code>pending</code>, <code>used</code>, <code>expired</code>, <code>cancelled</code>), <code>plate</code> e <code>limit</code> (até 500). Cada item traz o pagamento, a placa, o prazo e, se usada, quando, por qual câmera e o <code>event_id</code> do evento de saída.' });

  // ── PARTE D — webhook ────────────────────────────────────────────────────
  b.push({ t: 'parte', tag: 'Parte D', titulo: 'Webhook de placa reconhecida', dir: 'Nós chamamos · vocês recebem · endpoint definido por vocês' });

  b.push({ t: 'h2', id: 'requisicao', txt: 'A requisição' });
  b.push({ t: 'tabela', cab: ['', ''], linhas: [
    ['Método', '<code>POST</code> na URL que vocês informarem'],
    ['Content-Type', '<code>application/json</code>'],
    ['X-FastALPR-Event-Id', 'identificador único do evento, o mesmo do corpo'],
    ['X-FastALPR-Timestamp', 'época Unix em segundos, no momento da tentativa'],
    ['X-FastALPR-Signature', '<code>sha256=&lt;hmac&gt;</code> — ver a seção seguinte'],
    ['Tempo limite', '5 s por tentativa (ajustável de 0,5 s a 30 s)'],
  ]});

  b.push({ t: 'h2', id: 'assinatura', txt: 'Assinatura HMAC' });
  b.push({ t: 'p', txt: 'Cada requisição é assinada com HMAC-SHA256 usando um segredo compartilhado. O texto assinado é o <strong>timestamp, um ponto, e o corpo bruto da requisição</strong> — exatamente os bytes recebidos, antes de qualquer <em>parse</em>.' });
  b.push({ t: 'codigo', txt: '# texto assinado\n{X-FastALPR-Timestamp}.{corpo bruto}\n\n# cabeçalho enviado\nX-FastALPR-Signature: sha256=<hex do HMAC-SHA256>' });
  b.push({ t: 'h3', txt: 'Verificação em Node.js' });
  b.push({ t: 'codigo', rotulo: 'node', txt:
`const crypto = require('crypto');

// use o corpo BRUTO: express.raw({ type: 'application/json' })
function assinaturaConfere(corpoBruto, cabecalhos, segredo) {
  const ts = cabecalhos['x-fastalpr-timestamp'];
  const recebida = cabecalhos['x-fastalpr-signature'] || '';
  const esperada = 'sha256=' + crypto
    .createHmac('sha256', segredo)
    .update(ts + '.' + corpoBruto)
    .digest('hex');

  // comparação em tempo constante evita ataque de temporização
  const a = Buffer.from(recebida), b = Buffer.from(esperada);
  return a.length === b.length && crypto.timingSafeEqual(a, b);
}` });
  b.push({ t: 'h3', txt: 'Verificação em Python' });
  b.push({ t: 'codigo', rotulo: 'python', txt:
`import hmac, hashlib

def assinatura_confere(corpo_bruto: bytes, cabecalhos, segredo: str) -> bool:
    ts = cabecalhos['X-FastALPR-Timestamp']
    recebida = cabecalhos.get('X-FastALPR-Signature', '')
    texto = ts.encode() + b'.' + corpo_bruto
    esperada = 'sha256=' + hmac.new(segredo.encode(), texto, hashlib.sha256).hexdigest()
    return hmac.compare_digest(recebida, esperada)` });
  b.push({ t: 'nota', variante: 'aviso', txt: '<strong>Rejeitem timestamps antigos.</strong> Sugerimos recusar requisições cujo <code>X-FastALPR-Timestamp</code> esteja a mais de 5 minutos do relógio de vocês — protege contra reenvio de uma requisição capturada. Uma retentativa legítima chega sempre com timestamp e assinatura novos.' });

  b.push({ t: 'h2', id: 'payload', txt: 'O corpo do evento' });
  b.push({ t: 'p', txt: 'Exemplo de uma visita autorizada sendo reconhecida na entrada.' });
  b.push({ t: 'codigo', rotulo: 'evento', txt:
`{
  "event_id": "evt_f7720b33767042068e584f327a1c253a",
  "event_type": "${V.evento}",
  "occurred_at": "2026-09-10T11:02:47.398Z",
  "recognition": {
    "plate": "FRS1H32",
    "confidence": 0.94,
    "log_id": 21877
  },
  "camera": {
    "id": 3,
    "name": "CAM01-Entrada",
    "direction": "entrada",
    "ip_address": null
  },
  "vehicle": {
    "external_vehicle_id": null,
    "brand": null,
    "model": null
  },
  "person": {
    "classification": "visitor",
    "lookup_status": "authorized",
    "${V.campoIdentidade}": "${V.exemploCodigo}",
    "name": "Anderson Lima Pacheco",${V.temUnidade ? '\n    "unit": "501",\n    "block": "B",' : ''}
    "host_name": "Maria Helena Duarte"
  },
  "visit": {
    "authorization_id": "VISITA-00417",
    "status": "used",
    "host_type": "${V.hostPrincipal}",
    "purpose": "Manutenção hidráulica",
    "valid_until": "2026-09-10T18:00:00.000Z"
  },
  "snapshot": null
}` });

  b.push({ t: 'h3', txt: 'Dicionário de campos' });
  b.push({ t: 'tabela', cab: ['Campo', 'Tipo', 'Descrição'], linhas: [
    ['<code>event_id</code>', 'string', 'Identificador único, prefixo <code>evt_</code>. Use-o como chave de idempotência.'],
    ['<code>event_type</code>', 'string', `Sempre <code>${V.evento}</code>. O sufixo é a versão do contrato.`],
    ['<code>occurred_at</code>', 'ISO 8601 UTC', 'Momento do reconhecimento.'],
    ['<code>recognition.plate</code>', 'string', 'Placa normalizada, sem hífen. Mercosul ou padrão antigo.'],
    ['<code>recognition.confidence</code>', 'número 0–1', 'Confiança média do OCR na leitura escolhida.'],
    ['<code>recognition.log_id</code>', 'inteiro', 'Identificador da leitura no FastALPR, para rastrear a evidência.'],
    ['<code>camera.direction</code>', 'enum', '<code>entrada</code> ou <code>saida</code>.'],
    ['<code>vehicle.*</code>', 'string ou null', 'Vindos do cadastro que <strong>vocês</strong> enviaram. Nulos para visitante.'],
    ['<code>person.classification</code>', 'enum', `<code>${V.clAtivo}</code>, <code>${V.clInativo}</code> ou <code>visitor</code>.`],
    ['<code>person.lookup_status</code>', 'enum', '<code>active</code>, <code>inactive</code>, <code>authorized</code> ou <code>not_found</code>.'],
    [`<code>person.${V.campoIdentidade}</code>`, 'string ou null', `${V.temUnidade ? 'O código da unidade' : 'O código do associado'}, o mesmo que vocês enviaram.`],
    ['<code>person.host_name</code>', 'string ou null', 'Quem autorizou a visita, quando houver.'],
    ['<code>visit</code>', 'objeto ou null', '<strong>Nulo quando não é visita.</strong> Preenchido com a autorização que vale para esta passagem.'],
    ['<code>visit.status</code>', 'enum', '<code>pending</code> ou <code>used</code>. Numa entrada reconhecida, vem <code>used</code>.'],
    ['<code>snapshot</code>', 'objeto ou null', 'Foto do reconhecimento, quando habilitada.'],
    ['<code>stay</code>', 'objeto, só na saída', 'Desde 15/10/2026, em todo evento de <code>direction</code> <code>saida</code>: <code>entry_seen</code> (houve entrada lida da placa nas 24 h anteriores), <code>minutes</code> (minutos desde essa entrada; <code>null</code> sem ela), <code>tolerance_minutes</code> (a tolerância configurada na plataforma) e <code>within_tolerance</code> (<code>true</code> quando a permanência coube na tolerância — o veículo entrou e saiu sem usar o estacionamento; é o mesmo critério que acende o âmbar na guarita). Sem entrada lida, <code>within_tolerance</code> é <code>false</code>. Com a "Saída livre na tolerância" desligada nas Configurações (desde 18/10/2026), <code>tolerance_minutes</code> vem <code>0</code> e <code>within_tolerance</code> é sempre <code>false</code>: toda saída depende de liberação (pagamento, autorização de saída sem pagamento ou visitante autorizado).'],
    ['<code>gate</code>', 'objeto, só na saída', 'Desde 15/10/2026, com a liberação da saída pelo pagamento ligada: <code>decision</code> (<code>opened</code> = a plataforma abriria/abriu; <code>denied</code> = não), <code>reason</code> (<code>within_tolerance</code>, <code>visitor_authorized</code>, <code>paid</code>, <code>exempt</code>, <code>resident</code> (condomínio: morador ativo), <code>same_exit</code> (a mesma saída lida por outra câmera de saída até 5 min depois de um pagamento ou isenção usados); ou <code>no_authorization</code>, <code>authorization_expired</code>, <code>authorization_used</code>), <code>external_payment_id</code> (o pagamento usado), <code>mode</code> (<code>observation</code> = só registro, nenhum relé acionado; <code>automatic</code> = abertura automática ligada), <code>device</code> e <code>relay</code> (a controladora de saída e o relé que atendem a câmera, configurados na tela Câmeras; <code>null</code> sem controladora), <code>published</code> (<code>true</code> só quando a controladora confirmou o pulso de abertura — desde 18/10/2026 ela responde a cada comando; placa desligada ou sem rede = <code>false</code>).'],
    ['<code>correction</code>', 'objeto, só quando existe', 'Desde 14/10/2026: este evento <strong>corrige um anterior</strong> cuja placa foi lida errada e já tinha sido entregue. Traz <code>of_event_id</code> (o evento corrigido), <code>previous_plate</code> (a placa errada), <code>reason</code> (<code>passagem</code> para a correção automática, ou o usuário que revisou) e <code>corrected_at</code>. <strong>Não abra um ticket novo:</strong> localize o que foi aberto pelo evento <code>of_event_id</code> (o mesmo <code>recognition.log_id</code> vem nos dois) e troque a placa dele pela deste evento — ou cancele-o e abra o novo, se o seu sistema não permitir trocar a placa. Uma correção pode ser corrigida de novo (a placa volta à original): cada evento aponta para o imediatamente anterior pelo <code>of_event_id</code>. O evento errado que ainda não tinha saído da fila é descartado e não chega. Desde 15/10/2026 a correção automática nunca troca uma placa do cadastro por uma fora dele.'],
  ]});

  b.push({ t: 'h2', id: 'classificacoes', txt: 'As classificações' });
  b.push({ t: 'p', txt: 'A classificação sai do cruzamento da placa lida com o cadastro que vocês carregam pelas Partes A e B. Enquanto esse cadastro estiver vazio, <strong>toda placa chega como <code>visitor</code> com <code>not_found</code></strong>.' });
  b.push({ t: 'tabela', cab: ['Situação', 'classification', 'lookup_status', 'Identidade no evento'], linhas: [
    [V.descAtivo, `<code>${V.clAtivo}</code>`, '<code>active</code>', `${V.campoIdentidade}, nome, marca e modelo preenchidos`],
    [V.descInativo, `<code>${V.clInativo}</code>`, '<code>inactive</code>', 'preenchida, se a instalação permitir expor'],
    ['Visita autorizada e vigente', '<code>visitor</code>', '<code>authorized</code>', 'nome do visitante, quem autorizou e o bloco <code>visit</code>'],
    ['Placa fora do cadastro', '<code>visitor</code>', '<code>not_found</code>', 'tudo <code>null</code>'],
  ]});

  b.push({ t: 'h2', id: 'snapshot', txt: 'Foto do reconhecimento' });
  b.push({ t: 'p', txt: 'Quando a instalação estiver com o snapshot habilitado e com a URL base configurada, o campo <code>snapshot</code> traz um link assinado e temporário:' });
  b.push({ t: 'codigo', txt:
`{
  "snapshot": {
    "url": "https://.../evidencia?event=evt_...&expires=1757370000&signature=...",
    "expires_at": "2026-09-10T11:07:47.000Z",
    "sha256": "c1f0...9ab3"
  }
}` });
  b.push({ t: 'p', txt: 'A URL vale por <strong>5 minutos</strong> por padrão. O <code>sha256</code> é o resumo do arquivo — confere se a imagem baixada é a mesma que originou o evento.' });

  b.push({ t: 'h2', id: 'retentativas', txt: 'Resposta e retentativas' });
  b.push({ t: 'p', txt: 'Respondam <strong>qualquer código 2xx</strong> para confirmar o recebimento. Qualquer outro código, um tempo limite ou uma falha de conexão marcam a tentativa como malsucedida e o evento volta para a fila.' });
  b.push({ t: 'p', txt: 'O FastALPR persiste o evento <em>antes</em> de tentar entregar. Se o sistema de vocês estiver fora do ar, nada se perde: a fila segura e reenvia.' });
  b.push({ t: 'tabela', cab: ['Quando', 'Tentativa'], linhas: [
    ['imediata', '1ª, assim que o evento entra na fila'],
    ['+1 min', '2ª'],
    ['+5 min', '3ª'],
    ['+15 min', '4ª'],
    ['+60 min', '5ª'],
    ['+240 min', '6ª e as seguintes, até o limite de 10'],
  ]});
  b.push({ t: 'p', txt: 'Esgotadas as 10 tentativas, o evento fica marcado como <code>failed</code> e permanece consultável na tela <em>Integração e Outbox</em>, com o histórico de cada tentativa: código HTTP, resposta e duração. É possível reenviar manualmente.' });
  b.push({ t: 'nota', variante: 'info', txt: '<strong>Idempotência é responsabilidade do receptor.</strong> Uma retentativa reenvia o <em>mesmo</em> <code>event_id</code>. Guardem os identificadores já processados e devolvam 2xx sem reprocessar quando um repetido chegar — é o que evita duplicar um acesso por causa de uma resposta lenta.' });

  // Onda 66: a documentação passou a dizer onde se depura. O log já existia,
  // mas atrás da permissão de superadministrador — não fazia sentido citá-lo
  // para quem não podia abri-lo. Agora tem permissão própria e perfil de
  // consulta basta, então virou a primeira coisa a olhar quando a chamada não
  // se comporta como o esperado.
  b.push({ t: 'h2', id: 'depuracao', txt: 'Como depurar uma chamada' });
  b.push({ t: 'p', txt: 'Toda requisição que chega à API de Integração fica registrada na tela <em>Log de Requisições</em>, dentro da plataforma. Ela mostra o que o servidor <strong>de fato</strong> recebeu: data e hora, qual integração chamou, método, endpoint, código de resposta, duração e endereço de origem.' });
  b.push({ t: 'p', txt: 'É o primeiro lugar a olhar quando o comportamento não bate com o esperado, porque separa três casos que do lado de vocês parecem o mesmo:' });
  b.push({ t: 'tabela', cab: ['O que o log mostra', 'O que isso significa'], linhas: [
    ['a chamada não aparece', 'a requisição não chegou: URL, DNS, firewall ou a chamada nem saiu'],
    ['<code>401</code>', 'a chave não foi reconhecida — cabeçalho ausente, chave revogada ou de outra instalação'],
    ['<code>403</code>', 'a chave é válida, mas não tem o escopo que o endpoint exige'],
    ['<code>409</code>', 'conflito de dados — a resposta traz o motivo; é o caso da unidade repetida em blocos diferentes'],
    ['<code>429</code>', 'limite de chamadas por minuto da chave; espacem os lotes'],
    ['<code>2xx</code> mas o efeito não veio', 'a chamada foi aceita; confiram o corpo enviado e o retorno, não a conectividade'],
  ]});
  b.push({ t: 'nota', variante: 'info', txt: '<strong>O log não guarda o corpo das requisições</strong>, apenas o caminho chamado e o resultado. Ele também nunca mostra a chave — só o prefixo mascarado, o suficiente para saber qual credencial foi usada. Para inspecionar o que foi enviado, registrem do lado de vocês.' });
  b.push({ t: 'p', txt: 'Os eventos que <em>nós</em> enviamos a vocês, com o histórico de cada tentativa, ficam em outro lugar: a tela <em>Integração e Outbox</em>. O Log de Requisições é só o que entra.' });

  b.push({ t: 'h2', id: 'comecar', txt: 'Para começar' });
  b.push({ t: 'p', txt: 'Para a integração entrar em operação, precisamos de <strong>três informações</strong> de quem opera o sistema de gestão:' });
  b.push({ t: 'passos', itens: [
    'a <strong>URL do endpoint</strong> que receberá os eventos;',
    'o <strong>segredo compartilhado</strong> para a assinatura HMAC — podemos gerar e entregar;',
    'a <strong>URL base dos snapshots</strong>, se quiserem a foto dentro do evento.',
  ]});
  b.push({ t: 'p', txt: `Em troca, entregamos a <strong>API Key</strong> com os escopos <code>${V.escritaCadastro}</code> e <code>visitors:write</code> para iniciar a carga do cadastro. Tudo isso é configurado na tela <em>Integração e Outbox</em>.` });

  return { vertical: V, blocos: b };
}

export default documentacao;
